import { createClient } from '@/lib/supabase/client';

const FUNCTION_NAME = 'send-email';

async function invokeEmailFunction(payload: Record<string, unknown>): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.functions.invoke(FUNCTION_NAME, {
    body: payload,
  });
  if (error) {
    console.error('[EmailService] Failed to send email:', error.message);
    // Non-blocking — don't throw so callers aren't disrupted
  }
}

// ─── Password Reset ───────────────────────────────────────────────────────────

export async function sendPasswordResetEmail(params: {
  to: string;
  resetLink: string;
  userName?: string;
}): Promise<void> {
  await invokeEmailFunction({
    type: 'password_reset',
    to: params.to,
    resetLink: params.resetLink,
    userName: params.userName,
  });
}

// ─── Invite Code ──────────────────────────────────────────────────────────────

export async function sendInviteCodeEmail(params: {
  to: string;
  inviteCode: string;
  inviterName?: string;
  expiresAt?: string;
}): Promise<void> {
  await invokeEmailFunction({
    type: 'invite_code',
    to: params.to,
    inviteCode: params.inviteCode,
    inviterName: params.inviterName,
    expiresAt: params.expiresAt,
  });
}

// ─── Seasonal Event Launch ────────────────────────────────────────────────────

export async function sendSeasonalEventEmail(params: {
  to: string;
  userName?: string;
  eventName: string;
  eventDescription: string;
  themeEmoji: string;
  xpMultiplier?: number;
  bonusXp?: number;
  startsAt: string;
  endsAt: string;
  eventUrl?: string;
}): Promise<void> {
  await invokeEmailFunction({
    type: 'seasonal_event',
    ...params,
  });
}

// ─── Daily Challenge Reminder ─────────────────────────────────────────────────

export async function sendDailyChallengeEmail(params: {
  to: string;
  userName?: string;
  challenges: Array<{
    title: string;
    emoji: string;
    difficulty: string;
    xpReward: number;
    gameType: string;
  }>;
  totalXp: number;
  challengeUrl?: string;
}): Promise<void> {
  await invokeEmailFunction({
    type: 'daily_challenge',
    ...params,
  });
}

// ─── Bulk Broadcast (for admin use) ──────────────────────────────────────────

export async function broadcastSeasonalEventToUsers(
  userEmails: Array<{ email: string; displayName?: string }>,
  eventData: {
    eventName: string;
    eventDescription: string;
    themeEmoji: string;
    xpMultiplier?: number;
    bonusXp?: number;
    startsAt: string;
    endsAt: string;
  }
): Promise<{ sent: number; failed: number }> {
  let sent = 0;
  let failed = 0;

  // Send in batches of 5 to avoid rate limits
  const batchSize = 5;
  for (let i = 0; i < userEmails.length; i += batchSize) {
    const batch = userEmails.slice(i, i + batchSize);
    await Promise.allSettled(
      batch.map(async (user) => {
        try {
          await sendSeasonalEventEmail({
            to: user.email,
            userName: user.displayName,
            ...eventData,
          });
          sent++;
        } catch {
          failed++;
        }
      })
    );
    // Small delay between batches
    if (i + batchSize < userEmails.length) {
      await new Promise((r) => setTimeout(r, 200));
    }
  }

  return { sent, failed };
}

export async function broadcastDailyChallengeToUsers(
  userEmails: Array<{ email: string; displayName?: string }>,
  challengeData: {
    challenges: Array<{
      title: string;
      emoji: string;
      difficulty: string;
      xpReward: number;
      gameType: string;
    }>;
    totalXp: number;
  }
): Promise<{ sent: number; failed: number }> {
  let sent = 0;
  let failed = 0;

  const batchSize = 5;
  for (let i = 0; i < userEmails.length; i += batchSize) {
    const batch = userEmails.slice(i, i + batchSize);
    await Promise.allSettled(
      batch.map(async (user) => {
        try {
          await sendDailyChallengeEmail({
            to: user.email,
            userName: user.displayName,
            ...challengeData,
          });
          sent++;
        } catch {
          failed++;
        }
      })
    );
    if (i + batchSize < userEmails.length) {
      await new Promise((r) => setTimeout(r, 200));
    }
  }

  return { sent, failed };
}
